import { Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { BusinessConfigService } from '@admin-core/services/business-config.service';
import { MODULE_AI_MARKETING, MODULE_LIVE_CHAT, ModulesService } from '@admin-core/services/modules.service';
import {
  SettingsTab,
  StripeWebhookEndpoint,
  StripeWebhookEndpointsResponse
} from '@admin-core/models/business-config.model';
import { LanguageService } from '@admin-core/i18n/language.service';
import { TranslatePipe } from '@admin-core/i18n/translate.pipe';
import { ConfirmDialogService } from '@admin-shared/components/confirm-dialog/confirm-dialog.service';

@Component({
  selector: 'app-settings-admin',
  standalone: true,
  imports: [ReactiveFormsModule, TranslatePipe],
  templateUrl: './settings-admin.component.html',
  styleUrl: './settings-admin.component.scss'
})
export class SettingsAdminComponent implements OnInit {
  private readonly api = inject(BusinessConfigService);
  private readonly modules = inject(ModulesService);
  private readonly fb = inject(FormBuilder);
  private readonly i18n = inject(LanguageService);
  private readonly confirmDialog = inject(ConfirmDialogService);

  readonly tab = signal<SettingsTab>('features');
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly testingEmail = signal(false);
  readonly liveChat = signal(false);
  readonly aiMarketing = signal(false);
  readonly businessKey = signal('');
  readonly webhooksLoading = signal(false);
  readonly webhooksBusy = signal(false);
  readonly webhookEndpoints = signal<StripeWebhookEndpoint[]>([]);
  readonly webhookExpectedPath = signal('');
  readonly webhookSuggestedBaseUrls = signal<string[]>([]);

  readonly stripeForm = this.fb.nonNullable.group({
    publishableKey: [''],
    secretKey: [''],
    webhookSecret: ['']
  });

  readonly webhookForm = this.fb.nonNullable.group({
    baseUrl: ['']
  });

  readonly emailForm = this.fb.nonNullable.group({
    domain: [''],
    orderUserName: ['orden'],
    orderFromName: [''],
    passwordResetUserName: ['support'],
    passwordResetFromName: ['']
  });

  readonly brandForm = this.fb.nonNullable.group({
    displayName: ['']
  });

  readonly facebookForm = this.fb.nonNullable.group({
    appId: [''],
    appSecret: ['']
  });

  stripeHasSecretKey = false;
  stripeHasWebhookSecret = false;
  facebookHasAppSecret = false;

  ngOnInit(): void {
    this.load();
  }

  setTab(next: SettingsTab): void {
    this.tab.set(next);
    if (next === 'stripe') {
      this.loadWebhooks();
    }
  }

  load(): void {
    this.loading.set(true);
    this.api.getModules().subscribe({
      next: (modules) => {
        this.liveChat.set(modules.modules[MODULE_LIVE_CHAT] === true);
        this.aiMarketing.set(modules.modules[MODULE_AI_MARKETING] === true);
      },
      error: () => this.showError(this.i18n.t('settings.loadError'))
    });

    this.api.getSettings().subscribe({
      next: (settings) => {
        this.businessKey.set(settings.businessKey);
        this.stripeHasSecretKey = settings.stripe.hasSecretKey;
        this.stripeHasWebhookSecret = settings.stripe.hasWebhookSecret;
        this.stripeForm.patchValue({
          publishableKey: settings.stripe.publishableKey,
          secretKey: '',
          webhookSecret: ''
        });

        this.emailForm.patchValue({
          domain: settings.email.domain,
          orderUserName: settings.email.orderUserName,
          orderFromName: settings.email.orderFromName,
          passwordResetUserName: settings.email.passwordResetUserName,
          passwordResetFromName: settings.email.passwordResetFromName
        });
        this.brandForm.patchValue({
          displayName: settings.brand.displayName
        });
        this.facebookHasAppSecret = settings.facebook.hasAppSecret;
        this.facebookForm.patchValue({
          appId: settings.facebook.appId,
          appSecret: ''
        });
        this.loading.set(false);
        if (this.tab() === 'stripe') {
          this.loadWebhooks();
        }
      },
      error: () => {
        this.showError(this.i18n.t('settings.loadError'));
        this.loading.set(false);
      }
    });
  }

  loadWebhooks(): void {
    if (!this.stripeHasSecretKey) {
      this.webhookEndpoints.set([]);
      this.webhookExpectedPath.set('');
      this.webhookSuggestedBaseUrls.set([]);
      return;
    }

    this.webhooksLoading.set(true);
    this.api.listStripeWebhooks().subscribe({
      next: (response) => {
        this.applyWebhookResponse(response);
        this.webhooksLoading.set(false);
      },
      error: (err) => {
        this.webhooksLoading.set(false);
        this.showError(err?.error?.error ?? this.i18n.t('settings.stripe.webhooksLoadError'));
      }
    });
  }

  useSuggestedBaseUrl(url: string): void {
    this.webhookForm.patchValue({ baseUrl: url });
  }

  async createWebhook(): Promise<void> {
    const baseUrl = this.webhookForm.getRawValue().baseUrl.trim();
    if (!baseUrl) {
      this.showError(this.i18n.t('settings.stripe.webhookBaseUrlRequired'));
      return;
    }

    this.webhooksBusy.set(true);
    this.api.createStripeWebhook(baseUrl).subscribe({
      next: (response) => {
        this.applyWebhookResponse(response);
        this.webhookForm.patchValue({ baseUrl: '' });
        void this.confirmDialog.alert({
          title: this.i18n.t('common.alertTitle'),
          message: this.i18n.t('settings.stripe.webhookCreated')
        });
        this.webhooksBusy.set(false);
      },
      error: (err) => {
        this.showError(err?.error?.error ?? this.i18n.t('settings.stripe.webhookCreateError'));
        this.webhooksBusy.set(false);
      }
    });
  }

  async deleteWebhook(endpoint: StripeWebhookEndpoint): Promise<void> {
    const ok = await this.confirmDialog.confirm({
      title: this.i18n.t('common.confirmTitle'),
      message: this.i18n.t('settings.stripe.webhookDeleteConfirm', { url: endpoint.url }),
      confirmLabel: this.i18n.t('common.delete'),
      danger: true
    });
    if (!ok) {
      return;
    }

    this.webhooksBusy.set(true);
    this.api.deleteStripeWebhook(endpoint.id).subscribe({
      next: (response) => {
        this.applyWebhookResponse(response);
        this.webhooksBusy.set(false);
      },
      error: (err) => {
        this.showError(err?.error?.error ?? this.i18n.t('settings.stripe.webhookDeleteError'));
        this.webhooksBusy.set(false);
      }
    });
  }

  saveFeatures(): void {
    this.saving.set(true);
    this.api.updateModules({
      [MODULE_LIVE_CHAT]: this.liveChat(),
      [MODULE_AI_MARKETING]: this.aiMarketing()
    }).subscribe({
      next: (response) => {
        this.liveChat.set(response.modules[MODULE_LIVE_CHAT] === true);
        this.aiMarketing.set(response.modules[MODULE_AI_MARKETING] === true);
        void this.modules.load();
        this.notifySaved();
        this.saving.set(false);
      },
      error: (err) => {
        this.showError(err?.error?.error ?? this.i18n.t('settings.saveError'));
        this.saving.set(false);
      }
    });
  }

  saveStripe(): void {
    const value = this.stripeForm.getRawValue();
    const stripe: NonNullable<Parameters<BusinessConfigService['updateSettings']>[0]['stripe']> = {
      publishableKey: value.publishableKey
    };
    if (value.secretKey.trim()) {
      stripe.secretKey = value.secretKey;
    }
    if (value.webhookSecret.trim()) {
      stripe.webhookSecret = value.webhookSecret;
    }

    this.saving.set(true);
    this.api.updateSettings({ stripe }).subscribe({
      next: (settings) => {
        const hadSecret = this.stripeHasSecretKey;
        this.stripeHasSecretKey = settings.stripe.hasSecretKey;
        this.stripeHasWebhookSecret = settings.stripe.hasWebhookSecret;
        this.stripeForm.patchValue({ secretKey: '', webhookSecret: '' });
        this.notifySaved();
        this.saving.set(false);
        if (this.stripeHasSecretKey && (!hadSecret || this.tab() === 'stripe')) {
          this.loadWebhooks();
        }
      },
      error: (err) => {
        this.showError(err?.error?.error ?? this.i18n.t('settings.saveError'));
        this.saving.set(false);
      }
    });
  }

  saveEmail(): void {
    const value = this.emailForm.getRawValue();
    this.saving.set(true);
    this.api.updateSettings({ email: value }).subscribe({
      next: (settings) => {
        this.emailForm.patchValue({
          domain: settings.email.domain,
          orderUserName: settings.email.orderUserName,
          orderFromName: settings.email.orderFromName,
          passwordResetUserName: settings.email.passwordResetUserName,
          passwordResetFromName: settings.email.passwordResetFromName
        });
        this.notifySaved();
        this.saving.set(false);
      },
      error: (err) => {
        this.showError(err?.error?.error ?? this.i18n.t('settings.saveError'));
        this.saving.set(false);
      }
    });
  }

  sendTestEmail(): void {
    const value = this.emailForm.getRawValue();
    this.testingEmail.set(true);
    this.api.updateSettings({ email: value }).subscribe({
      next: (settings) => {
        this.emailForm.patchValue({
          domain: settings.email.domain,
          orderUserName: settings.email.orderUserName,
          orderFromName: settings.email.orderFromName,
          passwordResetUserName: settings.email.passwordResetUserName,
          passwordResetFromName: settings.email.passwordResetFromName
        });
        this.api.sendTestEmail({ mailbox: 'order' }).subscribe({
          next: (result) => {
            void this.confirmDialog.alert({
              title: this.i18n.t('common.alertTitle'),
              message: this.i18n.t('settings.email.testSent', {
                to: result.toEmail,
                from: result.from
              })
            });
            this.testingEmail.set(false);
          },
          error: (err) => {
            this.showError(err?.error?.error ?? this.i18n.t('settings.email.testError'));
            this.testingEmail.set(false);
          }
        });
      },
      error: (err) => {
        this.showError(err?.error?.error ?? this.i18n.t('settings.saveError'));
        this.testingEmail.set(false);
      }
    });
  }

  saveBrand(): void {
    const value = this.brandForm.getRawValue();
    this.saving.set(true);
    this.api.updateSettings({ brand: value }).subscribe({
      next: (settings) => {
        this.brandForm.patchValue({
          displayName: settings.brand.displayName
        });
        this.notifySaved();
        this.saving.set(false);
      },
      error: (err) => {
        this.showError(err?.error?.error ?? this.i18n.t('settings.saveError'));
        this.saving.set(false);
      }
    });
  }

  saveFacebook(): void {
    const value = this.facebookForm.getRawValue();
    const facebook: NonNullable<Parameters<BusinessConfigService['updateSettings']>[0]['facebook']> = {
      appId: value.appId
    };
    if (value.appSecret.trim()) {
      facebook.appSecret = value.appSecret;
    }

    this.saving.set(true);
    this.api.updateSettings({ facebook }).subscribe({
      next: (settings) => {
        this.facebookHasAppSecret = settings.facebook.hasAppSecret;
        this.facebookForm.patchValue({
          appId: settings.facebook.appId,
          appSecret: ''
        });
        this.notifySaved();
        this.saving.set(false);
      },
      error: (err) => {
        this.showError(err?.error?.error ?? this.i18n.t('settings.saveError'));
        this.saving.set(false);
      }
    });
  }

  private applyWebhookResponse(response: StripeWebhookEndpointsResponse): void {
    this.webhookEndpoints.set(response.endpoints);
    this.webhookExpectedPath.set(response.expectedPath);
    this.webhookSuggestedBaseUrls.set(response.suggestedBaseUrls);
    this.stripeHasWebhookSecret = response.hasWebhookSecret;
    if (!this.webhookForm.getRawValue().baseUrl.trim() && response.suggestedBaseUrls.length > 0) {
      this.webhookForm.patchValue({ baseUrl: response.suggestedBaseUrls[0] });
    }
  }

  private notifySaved(): void {
    void this.confirmDialog.alert({
      title: this.i18n.t('common.alertTitle'),
      message: this.i18n.t('settings.saved')
    });
  }

  private showError(message: string): void {
    void this.confirmDialog.alert({
      title: this.i18n.t('common.errorTitle'),
      message,
      danger: true
    });
  }
}
